import { Link, useLocation } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { useTheme } from '@voilajsx/uikit/theme-provider';
import { Moon, Sun, Github, Menu, X } from 'lucide-react';
import { useState } from 'react';

interface HeaderProps {
  showProgress?: boolean;
  progressValue?: number;
}

export const Header: React.FC<HeaderProps> = ({ showProgress, progressValue = 0 }) => {
  const location = useLocation();
  const { mode, toggleMode, theme, setTheme, availableThemes } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/tutorials', label: 'Tutorials' },
    { path: '/sandbox', label: 'Sandbox' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
      <div className="px-4 h-14 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <div className="w-7 h-7 bg-primary rounded flex items-center justify-center text-primary-foreground text-xs font-bold">
              UI
            </div>
            <span className="hidden sm:inline">UIKit</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-2">
          {/* Progress indicator */}
          {showProgress && (
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground mr-2">
              <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progressValue}%` }}
                />
              </div>
              <span className="text-xs">{progressValue}%</span>
            </div>
          )}

          {/* Theme selector */}
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value as typeof theme)}
            className="bg-background border border-input rounded px-2 py-1.5 text-sm hidden sm:block"
          >
            {availableThemes.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>

          {/* Dark mode toggle */}
          <Button variant="ghost" size="sm" onClick={toggleMode} className="h-8 w-8 p-0">
            {mode === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {/* GitHub link */}
          <a
            href="https://github.com/voilajsx/uikit"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex"
          >
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <Github className="w-4 h-4" />
            </Button>
          </a>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden h-8 w-8 p-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-background px-4 py-3">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="https://github.com/voilajsx/uikit"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/50 flex items-center gap-2"
            >
              <Github className="w-4 h-4" />
              GitHub
            </a>
          </nav>
          <div className="mt-3 pt-3 border-t border-border">
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as typeof theme)}
              className="w-full bg-background border border-input rounded px-3 py-2 text-sm"
            >
              {availableThemes.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)} Theme
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </header>
  );
};
